'use client';

import { SvgTon } from '@bazaar-marketplace/assets/ton';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Button } from '~/components';
import { TgStarAmount } from '~/components/icons/tg-star-icon';
import { CountSelector } from '~/components/ui/count-selector';
import { useUserEventsStream } from '~/hooks/use-user-events-stream';
import type { DirectSaleOfferResponseDto } from '~/lib/api';
import { useDirectSaleOffersControllerCreateDirectSalePurchase, useUsersControllerGetProfile } from '~/lib/api';
import { getErrorMessage } from '~/lib/get-error-message';
import { openTelegramOrRegularLink } from '~/lib/open-link';
import { useLatestValueRef } from '~/lib/use-latest-value-ref';
import type { PackDeliveredEvent } from '~/lib/user-event.schema';
import { packDeliveredEventSchema } from '~/lib/user-event.schema';
import { usePackNotificationStore } from '~/store/purchase-store';
import type { SaleButtonStatus } from './sale-buy-button';
import { SelectDirectSalePaymentMethod } from './select-direct-sale-payment-method';

type PurchaseFlowState = 'error' | 'idle' | 'purchasing';
type PaymentMethod = 'telegram_stars' | 'ton_wallet';

interface DirectSalePurchaseButtonProps {
  offer: DirectSaleOfferResponseDto;
  status: SaleButtonStatus;
}

export function DirectSalePurchaseButton({ offer, status }: DirectSalePurchaseButtonProps) {
  const t = useTranslations('directSale');

  const queryClient = useQueryClient();

  // Global store only for notifying about delivered packs
  const { addDeliveredPacks } = usePackNotificationStore();

  const [selectedPackCount, setSelectedPackCount] = useState(1);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>(
    offer.tonPricePerPack != null ? 'ton_wallet' : 'telegram_stars',
  );
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [packDeliveredEvent, setPackDeliveredEvent] = useState<PackDeliveredEvent>();

  const [purchaseFlowState, setPurchaseFlowState] = useState<PurchaseFlowState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { mutate: createPurchase, isPending } = useDirectSaleOffersControllerCreateDirectSalePurchase({
    mutation: {
      onSuccess: (response) => {
        setPurchaseFlowState('purchasing');
        setPurchaseId(response.data.purchaseId);
        void queryClient.invalidateQueries();
        if (response.data.invoiceUrl) {
          openTelegramOrRegularLink(response.data.invoiceUrl);
        }
      },
      onError: (error) => {
        setErrorMessage(getErrorMessage(error));
        setPurchaseFlowState('error');
      },
    },
  });

  function openPackDeliveredResultStep(packEvent: PackDeliveredEvent) {
    // Add delivered packs to global notification store
    const packIds = packEvent.data.packIds;
    addDeliveredPacks(packEvent.data.purchaseId, packIds);

    // Reset local state
    setPurchaseFlowState('idle');
    setErrorMessage(null);
  }

  function onPurchaseIdChange(purchaseId: string) {
    // in case of TON purchase, the event might arrive sooner than success response,
    // so we check if we received the event for this purchase here
    if (packDeliveredEvent?.data.purchaseId === purchaseId) {
      openPackDeliveredResultStep(packDeliveredEvent);
    }
  }

  const latestRef = useLatestValueRef({
    onPurchaseIdChange,
  });

  useEffect(() => {
    if (purchaseId) {
      latestRef.current.onPurchaseIdChange(purchaseId);
    }
  }, [purchaseId, latestRef]);

  const getSelectedPrice = () => {
    if (selectedPaymentMethod === 'telegram_stars') {
      return Number(offer.starPricePerPack);
    } else {
      // Use backend-provided TON price, fallback to 0 if not available
      return offer.tonPricePerPack ? Number(offer.tonPricePerPack) : 0;
    }
  };

  const totalPrice = getSelectedPrice() * selectedPackCount;

  // Get current user
  const { data: userData } = useUsersControllerGetProfile();
  const userId = userData?.data.id;

  // Start SSE connection when sheet opens (before purchase)
  useUserEventsStream({
    userId,
    onEvent: (event) => {
      const packEvent = packDeliveredEventSchema.safeParse(event).data;
      if (packEvent?.data.purchaseId) {
        setPackDeliveredEvent(packEvent);
        if (packEvent.data.purchaseId === purchaseId) {
          openPackDeliveredResultStep(packEvent);
        }
      }
    },
  });

  function onPurchaseClick() {
    setErrorMessage(null);
    createPurchase({
      id: offer.id,
      data: {
        packsCount: selectedPackCount,
        paymentMethod: selectedPaymentMethod,
      },
    });
  }

  const getButtonText = () => (
    <div className="flex items-center gap-0.5">
      {t('getFor')}
      <div className="pl-0.5 flex items-center">
        {selectedPaymentMethod === 'telegram_stars' ? (
          <TgStarAmount amount={totalPrice} />
        ) : (
          <div className="inline-flex items-center gap-0.5">
            <SvgTon className="size-5" />

            {totalPrice.toFixed(2)}
          </div>
        )}
      </div>
    </div>
  );

  const isButtonDisabled = isPending || status !== 'active' || purchaseFlowState === 'purchasing';
  const isPaymentMethodSelectionEnabled = [offer.starPricePerPack, offer.tonPricePerPack].filter((x) => !!x).length > 1;
  const isInfiniteSupply = offer.infiniteSupply === 'generate_on_sale';
  const INFINITE_SUPPLY_MAX = 99;

  return (
    <div className="flex flex-col gap-4 pb-8">
      <div className="grid grid-cols-[1fr_max-content] gap-10">
        <div className={!isPaymentMethodSelectionEnabled ? 'invisible' : ''}>
          <SelectDirectSalePaymentMethod
            currentMethod={selectedPaymentMethod}
            starPricePerPack={offer.starPricePerPack}
            tonPricePerPack={offer.tonPricePerPack}
            onChange={setSelectedPaymentMethod}
          />
        </div>
        <CountSelector
          buttonGridClassName="grid-cols-[48px_56px_48px]"
          disabled={purchaseFlowState !== 'idle'}
          maxValue={isInfiniteSupply ? INFINITE_SUPPLY_MAX : offer.inStock}
          minValue={1}
          value={selectedPackCount}
          onChange={setSelectedPackCount}
        />
      </div>

      {errorMessage ? (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
          <div className="text-red-400 text-sm text-center font-bold">{errorMessage}</div>
        </div>
      ) : null}

      <Button className="w-full" disabled={isButtonDisabled} type="button" variant="rainbow" onClick={onPurchaseClick}>
        {getButtonText()}
      </Button>
    </div>
  );
}
